/**
 * RolSekmesi.jsx — Kullanıcı Kartı › Rol (backlog #379).
 *
 * Kullanıcının rolü (grubu) ve firmalarındaki fuar rolleri; "Rolü Değiştir" `PUT /users/{id}/group` (userService.updateUserGroup,
 * MANAGE_USER_ROLES — Admin, kök de geçer). Backend kuralları arkada durur ve reddi olduğu gibi gösterilir: personel (supervisor)
 * rolüne girmek / çıkmak ayrıca rbac.manage ister (yalnızca kök), tek kök rolü korunur, kimse kendi rolünü değiştiremez.
 *
 * Personel sınırını geçen değişiklikte uyarı Yetki Yönetimi › Kullanıcılar'ın (Bilge, #309) uyarılarıyla aynıdır (`rolSekmesi.personelOluyor`,
 * Yeni Kullanıcı formu da aynı metni kullanır). Rol listesi
 * `GET /users/groups/all`dan gelir (personel yetkisi yeter; `GET /rbac/groups` yalnızca kök). Satırı `{ id, userGroupName, staff }` taşır
 * (UserGroupDTO.isStaff, #307): hedef rolün personel olup olmadığı buradan bilinir. Kök rol (id 1) seçenek olarak sunulmaz — tek kök
 * yalnızca Kök Devri ile el değiştirir (backend de reddeder).
 */
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { Building2, UserCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";
import { getUserGroups, updateUserGroup } from "../../../../services/userService";
import { getMyPermissions } from "../../../../services/rbacService";
import { adSoyad, tarafAdi, tarafMeta } from "../../../../Components/Kullanici/KullaniciParcalari";

const KOK_ROL_ID = 1;

async function rolleriOku() {
  const gruplar = await getUserGroups();
  return (gruplar || []).map((g) => ({ id: g.id, ad: g.userGroupName, staff: Boolean(g.staff), root: g.id === KOK_ROL_ID }));
}

function RolDegistirModal({ kullanici: u, onClose, onSaved, kendisi }) {
  const { t } = useTranslation("yonetim");
  const [roller, setRoller] = useState(null);
  const [hedef, setHedef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const r = await rolleriOku();
        if (!ignore) setRoller(r);
      } catch (e) {
        if (!ignore) { setRoller([]); setError(e.message || i18n.t("yonetim:rolSekmesi.rollerOkunamadi")); }
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  const secenekler = (roller || []).filter((r) => !r.root && r.id !== u.userGroupId);
  const hedefRol = secenekler.find((r) => String(r.id) === String(hedef));
  const personelOluyor = Boolean(hedefRol) && hedefRol.staff && !u.staff;
  const personeldenCikiyor = Boolean(hedefRol) && !hedefRol.staff && u.staff;

  async function kaydet() {
    setBusy(true);
    setError(null);
    try {
      await updateUserGroup(u.userId, Number(hedef));
      await onSaved(hedefRol?.ad);
    } catch (e) {
      setError(e.message || t("rolSekmesi.rolDegistirilemedi"));
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !busy && onClose()} centered>
      <ModalHeader toggle={() => !busy && onClose()}>{t("rolSekmesi.rolDegistirBaslik", { ad: adSoyad(u) })}</ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}
        {kendisi && <Alert fade={false} color="warning" className="small">{t("rolSekmesi.kendiRolunuzuDegistiremezsiniz")}</Alert>}
        <p className="mb-2 small">{t("rolSekmesi.suAnkiRol")} <span className="fw-semibold">{u.userGroupName || "—"}</span></p>
        <Label for="kk-rol-hedef" className="form-label">{t("rolSekmesi.yeniRol")}</Label>
        {roller == null ? (
          <div><Spinner size="sm" color="primary" /></div>
        ) : (
          <Input id="kk-rol-hedef" type="select" value={hedef} disabled={busy || kendisi} onChange={(e) => setHedef(e.target.value)}>
            <option value="">{t("rolSekmesi.rolSecin")}</option>
            {secenekler.map((r) => <option key={r.id} value={r.id}>{r.ad}</option>)}
          </Input>
        )}
        {personelOluyor && <Alert fade={false} color="danger" className="small mt-3 mb-0">{t("rolSekmesi.personelOluyor")}</Alert>}
        {personeldenCikiyor && <Alert fade={false} color="warning" className="small mt-3 mb-0">{t("rolSekmesi.personeldenCikiyor")}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={busy} onClick={onClose}>{t("rolSekmesi.vazgec")}</Button>
        <Button color={personelOluyor ? "danger" : "primary"} disabled={busy || !hedef || kendisi} onClick={kaydet}>
          {busy ? <Spinner size="sm" /> : t("rolSekmesi.rolDegistir")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * @param {object}   kart       `GET /users/{id}/kart` yanıtı
 * @param {Function} onChanged  `(mesaj)` — rol değişti; sayfa kartı yeniden okur
 */
export default function RolSekmesi({ kart, onChanged }) {
  const { t } = useTranslation("yonetim");
  const u = kart.kullanici;
  const taraf = tarafMeta(u);
  const firmalar = u.firmalar || [];
  const [acik, setAcik] = useState(false);
  const [ben, setBen] = useState(null);

  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const me = await getMyPermissions();
        if (!ignore) setBen(me);
      } catch {
        if (!ignore) setBen(null);
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  const kendisi = !ben?.root && ben?.userId === u.userId;
  const engel = u.root ? t("rolSekmesi.kokYalnizcaKokDevriyle") : kendisi ? t("rolSekmesi.kendiRolunuzuDegistiremezsiniz") : null;

  return (
    <>
      <Card className="mb-3">
        <CardHeader className="bg-transparent d-flex align-items-center gap-2">
          <UserCog size={16} className="text-muted" />
          <span className="fw-semibold">{t("rolSekmesi.rol")}</span>
          <span title={engel || t("rolSekmesi.baskaRoleTasi")} className="ms-auto">
            <Button color="primary" outline size="sm" disabled={Boolean(engel)} onClick={() => setAcik(true)}>
              {t("rolSekmesi.rolDegistirDugme")}
            </Button>
          </span>
        </CardHeader>
        <CardBody>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-bold fs-5">{u.userGroupName || t("rolSekmesi.rolsuz")}</span>
            <span className="badge text-white fw-normal" style={{ background: taraf.hex }}>{tarafAdi(taraf)}</span>
          </div>
        </CardBody>
      </Card>

      {firmalar.length > 0 && (
        <Card className="mb-3">
          <CardHeader className="bg-transparent d-flex align-items-center gap-2">
            <Building2 size={16} className="text-muted" />
            <span className="fw-semibold">{t("rolSekmesi.firmaVeFuarRolleri")}</span>
            <Badge color="light" className="text-body border fw-normal">{firmalar.length}</Badge>
          </CardHeader>
          <CardBody className="p-0">
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light text-muted small">
                  <tr><th className="ps-3">{t("rolSekmesi.firma")}</th><th>{t("rolSekmesi.fuarRolleri")}</th></tr>
                </thead>
                <tbody>
                  {firmalar.map((f) => (
                    <tr key={f.firmaId}>
                      <td className="ps-3 fw-semibold">{f.firmaName || t("rolSekmesi.firmaNo", { id: f.firmaId })}</td>
                      <td className="small">
                        {(f.fuarRolAdlari || []).length > 0 ? f.fuarRolAdlari.join(", ") : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {acik && (
        <RolDegistirModal
          kullanici={u} kendisi={kendisi} onClose={() => setAcik(false)}
          onSaved={async (rolAdi) => {
            setAcik(false);
            onChanged?.(rolAdi
              ? t("rolSekmesi.rolDegistiAdli", { ad: adSoyad(u), rol: rolAdi })
              : t("rolSekmesi.rolDegisti", { ad: adSoyad(u) }));
          }}
        />
      )}
    </>
  );
}
